import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Code, Copy, Check } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export default function SchemaGenerator({ businessId, open, onOpenChange }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/businesses/${businessId}/schema`)
      .then((r) => setSchema(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  }, [open, businessId]);

  const copy = async () => {
    if (!schema) return;
    try {
      await navigator.clipboard.writeText(schema.script_tag);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = schema.script_tag;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
    }
    setCopied(true);
    toast.success("Schema copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="schema-generator">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Code size={22} className="text-[#0055FF]" /> LocalBusiness Schema
          </SheetTitle>
          <SheetDescription>
            Paste this structured-data snippet into the <code className="text-slate-700">&lt;head&gt;</code> of your website.
            It helps Google and AI assistants understand and cite your business accurately.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 h-64 rounded-xl bg-slate-50 border border-slate-200 animate-pulse" />
        ) : schema ? (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">JSON-LD snippet</p>
              <Button onClick={copy} data-testid="schema-copy-button"
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white h-9">
                {copied ? <Check size={16} className="mr-1.5" /> : <Copy size={16} className="mr-1.5" />}
                {copied ? "Copied" : "Copy code"}
              </Button>
            </div>
            <pre data-testid="schema-code"
              className="rounded-xl bg-slate-900 text-slate-100 text-xs leading-relaxed p-4 overflow-x-auto whitespace-pre-wrap break-words">
{schema.script_tag}
            </pre>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
              <p className="font-semibold text-slate-900 mb-1">How to use it</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>Copy the code above.</li>
                <li>Paste it inside the <code>&lt;head&gt;</code> section of your homepage.</li>
                <li>Validate it with Google's Rich Results Test, then mark Schema.org Live in your directory list.</li>
              </ol>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
